import { Loader2, AlertCircle } from "lucide-react";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { AccountDetailModal } from "../components/AccountDetailModal";
import { AccountSection } from "../components/AccountSection";
import { AccountsWalletsFilters } from "../components/AccountsWalletsFilters";
import { AccountsWalletsHeader } from "../components/AccountsWalletsHeader";
import { AccountsWalletsShowMore } from "../components/AccountsWalletsShowMore";
import { AddAccountModal } from "../components/AddAccountModal";
import { DeletePublicAccountModal } from "../components/DeletePublicAccountModal";
import { type CurrencyFilterValue } from "../constants/accounts-wallets.constants";
import { SEARCH_DEBOUNCE_MS } from "../constants/public-accounts.constants";
import { useAccountsWalletsPage } from "../hooks/useAccountsWalletsPage";
import { publicAccountsService } from "../services/publicAccounts.service";
import type { AccountSectionDisplay } from "../types/accounts-wallets.types";
import type { NewPublicAccountFormData } from "../types/new-public-account.types";
import {
  canAddPublicAccount,
  canEditPublicAccount,
  canDeletePublicAccount,
} from "../utils/publicAccountsPermissions";

const FILTER_TRANSITION_MS = 280;

function filterSectionsByCurrency(
  sections: AccountSectionDisplay[],
  currency: CurrencyFilterValue,
): AccountSectionDisplay[] {
  if (currency === "ALL") return sections;
  return sections
    .map((section) => ({
      ...section,
      accounts: section.accounts.filter((a) => a.currency.toUpperCase() === currency),
    }))
    .filter((s) => s.accounts.length > 0)
    .map((s) => ({ ...s, accountCount: s.accounts.length }));
}

export const AccountsWalletsPage = () => {
  const { t } = useTranslations("accountsWallets");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_wallets"), to: "/app/accounts-wallets" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const { user } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyFilterValue>("ALL");
  const [displayCurrency, setDisplayCurrency] = useState<CurrencyFilterValue>("ALL");
  const [isFilterExiting, setIsFilterExiting] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterTransitionRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    sections,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    hasNoAccess,
    searchOverlayKeep,
    fetchAccounts,
    loadMore,
  } = useAccountsWalletsPage(searchParam);

  const canAddAccount = canAddPublicAccount(user);
  const canEditAccount = canEditPublicAccount(user);
  const canDeleteAccount = canDeletePublicAccount(user);

  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [detailAccountId, setDetailAccountId] = useState<string | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchParam(searchValue);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchValue]);

  const handleCurrencyChange = useCallback(
    (currency: CurrencyFilterValue) => {
      setSelectedCurrency(currency);
      if (currency === displayCurrency) return;
      if (filterTransitionRef.current) clearTimeout(filterTransitionRef.current);
      setIsFilterExiting(true);
      filterTransitionRef.current = setTimeout(() => {
        setDisplayCurrency(currency);
        setIsFilterExiting(false);
        filterTransitionRef.current = null;
      }, FILTER_TRANSITION_MS);
    },
    [displayCurrency],
  );

  useEffect(() => {
    return () => {
      if (filterTransitionRef.current) clearTimeout(filterTransitionRef.current);
    };
  }, []);

  const filteredSections = useMemo(
    () => filterSectionsByCurrency(sections, displayCurrency),
    [sections, displayCurrency],
  );

  const handleAddAccount = useCallback(() => {
    setEditingAccountId(null);
    setIsAddAccountModalOpen(true);
  }, []);

  const handleAddAccountSubmit = useCallback(
    async (data: NewPublicAccountFormData, accountId?: string) => {
      if (accountId) {
        await publicAccountsService.update(accountId, data);
        colocaToast.success(t("success_update"));
      } else {
        await publicAccountsService.create(data);
        colocaToast.success(t("success_create"));
      }
      await fetchAccounts(searchParam);
    },
    [searchParam, fetchAccounts, t],
  );

  const handleAccountClick = useCallback((id: string) => {
    setDetailAccountId(id);
  }, []);

  const handleEditAccount = useCallback((id: string) => {
    setDetailAccountId(null);
    setEditingAccountId(id);
    setIsAddAccountModalOpen(true);
  }, []);

  const handleDeleteAccountRequest = useCallback((id: string, name: string) => {
    setDeleteTarget({ id, name });
  }, []);

  const handleDeleteAccountConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    try {
      await publicAccountsService.delete(id);
      colocaToast.success(t("success_delete"));
      setDeleteTarget(null);
      setDetailAccountId((current) => (current === id ? null : current));
      await fetchAccounts(searchParam);
    } catch (err) {
      colocaToast.error(HttpApiService.getErrorMessage(err) ?? t("error_delete"));
      throw err;
    }
  }, [deleteTarget, searchParam, fetchAccounts, t]);

  const handleAddAccountModalClose = useCallback(() => {
    setIsAddAccountModalOpen(false);
    setEditingAccountId(null);
  }, []);

  if (hasNoAccess) {
    return <NoAccessView />;
  }

  if (isLoading && sections.length === 0) {
    return (
      <div className="flex flex-col flex-1 bg-slate-50 dark:bg-zinc-950 items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-green-500 mb-4" />
        <p className="text-slate-600 dark:text-slate-400">{t("loading_accounts")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 flex-1 bg-slate-50 dark:bg-zinc-950">
      <AccountsWalletsHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onAddAccountClick={canAddAccount ? handleAddAccount : undefined}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 w-full space-y-12 flex-1">
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-red-700 dark:text-red-300">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        <AccountsWalletsFilters selectedCurrency={selectedCurrency} onCurrencyChange={handleCurrencyChange} />

        <div
          className="transition-opacity ease-out"
          style={{
            opacity: isFilterExiting ? 0 : 1,
            transitionDuration: `${FILTER_TRANSITION_MS}ms`,
            pointerEvents: isFilterExiting ? "none" : "auto",
          }}
        >
          {filteredSections.length === 0 ? (
            <div className="text-center py-16 text-slate-500 dark:text-zinc-400">
              {sections.length === 0 ? (
                searchParam.trim() ? (
                  <>
                    <p className="font-medium text-slate-700 dark:text-slate-300">{t("no_match_search")}</p>
                    <p className="text-sm mt-2 max-w-sm mx-auto">{t("try_other_terms")}</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      {t("no_accounts_registered")}
                    </p>
                    <p className="text-sm mt-2 max-w-sm mx-auto">{t("no_accounts_description")}</p>
                  </>
                )
              ) : (
                <>
                  <p className="font-medium">{t("no_match_currency")}</p>
                  <p className="text-sm mt-1">{t("adjust_currency_filter")}</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                <div
                  className={`space-y-12 transition-opacity duration-500 ease-out ${
                    (isLoading && sections.length > 0) || searchOverlayKeep
                      ? "opacity-50 pointer-events-none"
                      : "opacity-100"
                  }`}
                >
                  {filteredSections.map((section) => (
                    <AccountSection
                      key={section.id}
                      section={section}
                      onAccountClick={handleAccountClick}
                      onEditClick={canEditAccount ? handleEditAccount : undefined}
                      onDeleteClick={canDeleteAccount ? handleDeleteAccountRequest : undefined}
                    />
                  ))}

                  {hasMore && <AccountsWalletsShowMore onShowMore={loadMore} isLoadingMore={isLoadingMore} />}
                </div>
                {((isLoading && sections.length > 0) || searchOverlayKeep) && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/40 dark:bg-zinc-900/40 backdrop-blur-[3px] min-h-[200px] transition-opacity duration-500 ease-out"
                    aria-live="polite"
                    aria-busy={isLoading}
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-green-500" />
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t("searching")}</p>
                  </div>
                )}
              </div>

              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-green-500" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddAccountModal
        isOpen={isAddAccountModalOpen}
        onClose={handleAddAccountModalClose}
        onSubmit={handleAddAccountSubmit}
        accountId={editingAccountId}
      />

      <AccountDetailModal
        isOpen={detailAccountId !== null}
        onClose={() => setDetailAccountId(null)}
        accountId={detailAccountId}
        showCopyStatsSection={false}
      />

      <DeletePublicAccountModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        accountName={deleteTarget?.name ?? ""}
        onConfirm={handleDeleteAccountConfirm}
      />
    </div>
  );
};
